// ═══════════════════════════════════════════════════════════════════════════
// b2mmlFederationTab.jsx — Layered Federation via B2MML / ISA-95 Service Bus
// ═══════════════════════════════════════════════════════════════════════════
// Views:
//   1. 🗺  Federation Map     — SVG bus diagram, levels as swim lanes,
//                                B2MML topics on the wire
//   2. 📚 Schemas (8)         — every B2MML schema with key classes, rules
//   3. 🤝 Contracts            — level-to-level federation contracts
//   4. 📨 XML Payloads        — annotated wire-format examples
//   5. 🔌 Gateways            — per-tier B2MML gateways and their roles
//   6. 🎯 Conformance         — how to certify a new flow against B2MML
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { T, FONTS, chipBtn, pill } from "./theme";
import {
  B2MML_SCHEMAS, FEDERATION_CONTRACTS, BUS_TOPOLOGY, XML_PAYLOADS,
  ISA95_LEVELS, B2MML_STATS,
  schemaById, contractsBySchema, payloadsBySchema, payloadById,
  interfacesForSchema,
} from "./b2mmlData";
import { EntityRefList } from "./navigation";

const VIEWS = [
  { id: "map",         label: "🗺 Federation map",       color: T.cyan },
  { id: "live",        label: "🌊 Live B2MML flow",      color: T.green },
  { id: "schemas",     label: "📚 Schemas (8)",          color: T.purple },
  { id: "contracts",   label: "🤝 Contracts",            color: T.green },
  { id: "payloads",    label: "📨 XML payloads",         color: T.amber },
  { id: "gateways",    label: "🔌 Gateways",             color: T.blue },
  { id: "conformance", label: "🎯 Conformance",          color: T.gold },
];

export default function B2MMLFederationTab() {
  const [view, setView] = useState("map");
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedPayload, setSelectedPayload] = useState("XML-OPSCHED-001");

  return (
    <div style={{
      padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto",
      fontFamily: FONTS.body,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🏛 Layered Federation · ISA-95 / B2MML Service Bus
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          Each ISA-95 level is a sovereign tier. Federation runs on a level-segmented service bus carrying
          canonical B2MML XML payloads. No cross-tier joins; every link is a versioned schema contract.
        </div>
        <div style={{ fontSize: 9.5, color: T.amber, marginTop: 6, fontStyle: "italic" }}>
          "Each tier owns its master data. Tiers exchange contracts, not databases."
        </div>
      </div>

      {/* Stat strip */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        {[
          { l: "B2MML schemas",   v: B2MML_STATS.totalSchemas,   c: T.purple },
          { l: "Federation contracts", v: B2MML_STATS.totalContracts, c: T.green },
          { l: "Tier-1 (exactly-once)", v: B2MML_STATS.tier1Contracts, c: T.red },
          { l: "Bidirectional",   v: B2MML_STATS.bidirectional,  c: T.blue },
          { l: "Bus segments",    v: B2MML_STATS.busSegments,    c: T.cyan },
          { l: "Gateways",        v: B2MML_STATS.gateways,       c: T.amber },
          { l: "XML samples",     v: B2MML_STATS.totalPayloads,  c: T.gold },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 6,
            padding: "8px 14px", textAlign: "center",
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 7.5, color: T.text2, letterSpacing: 1, textTransform: "uppercase" }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* View tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)} style={chipBtn(view === v.id, v.color)}>
            {v.label}
          </button>
        ))}
      </div>

      {view === "map"         && <FederationMap setView={setView} setSelectedSchema={setSelectedSchema}/>}
      {view === "live"        && <LiveB2MMLFlow setView={setView} setSelectedPayload={setSelectedPayload}/>}
      {view === "schemas"     && <SchemasView selectedSchema={selectedSchema} setSelectedSchema={setSelectedSchema}
                                              setView={setView} setSelectedPayload={setSelectedPayload}/>}
      {view === "contracts"   && <ContractsView setView={setView} setSelectedPayload={setSelectedPayload}/>}
      {view === "payloads"    && <PayloadsView selectedPayload={selectedPayload} setSelectedPayload={setSelectedPayload}/>}
      {view === "gateways"    && <GatewaysView/>}
      {view === "conformance" && <ConformanceView/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 1 · FEDERATION MAP · the SVG bus diagram
// ═══════════════════════════════════════════════════════════════════════════
function FederationMap({ setView, setSelectedSchema }) {
  // Canvas
  const W = 1200;
  const LANE_H = 110;
  const H = 70 + LANE_H * 5 + 40;

  // Lane Y centres
  const laneY = (idx) => 70 + LANE_H * idx + LANE_H / 2;

  // Enterprise bus position (between L4 and L3)
  const ENT_BUS_Y = 70 + LANE_H + 20;
  // Plant bus position (between L3 and L2)
  const PLANT_BUS_Y = 70 + LANE_H * 2 + 20;
  // Fieldbus (between L2 and L1)
  const FIELD_BUS_Y = 70 + LANE_H * 3 + 20;

  // L4 systems
  const L4_SYS = [
    { x: 140, label: "SAP ERP",       sub: "Orders · Material · Cost", color: T.gold },
    { x: 340, label: "Teamcenter",    sub: "Product · BOM · ECO",      color: T.cyan },
    { x: 540, label: "APS",            sub: "Planning · capability",    color: T.amber },
    { x: 740, label: "Insights Hub",  sub: "Analytics · ML",           color: T.purple },
    { x: 940, label: "HR / IAM",      sub: "Personnel master",         color: T.purple },
  ];
  // L4 gateways (sit on the enterprise bus)
  const L4_GW = [
    { x: 140, label: "Orders GW",     handles: ["OPERATIONS_SCHEDULE","OPERATIONS_PERFORMANCE","OPERATIONS_CAPABILITY"], color: T.green },
    { x: 340, label: "Material GW",   handles: ["MATERIAL"], color: T.gold },
    { x: 540, label: "Asset GW",      handles: ["EQUIPMENT"], color: T.amber },
    { x: 740, label: "Cloud GW",      handles: [], color: T.purple },
    { x: 940, label: "Personnel GW", handles: ["PERSONNEL"], color: T.purple },
  ];
  // L3 ingress / egress gateways
  const L3_GW = [
    { x: 240, label: "MES Ingress",   handles: ["PERSONNEL","EQUIPMENT","MATERIAL","OPERATIONS_SCHEDULE","OPERATIONS_CAPABILITY"], color: T.cyan },
    { x: 540, label: "MES Egress",    handles: ["OPERATIONS_PERFORMANCE","MATERIAL","EQUIPMENT"], color: T.green },
    { x: 840, label: "Dispatch GW",   handles: ["OPERATIONS_DEFINITION","MATERIAL"], color: T.blue },
  ];
  // L3 systems
  const L3_SYS = [
    { x: 240, label: "Opcenter EX",   sub: "MES · execution",        color: T.blue },
    { x: 540, label: "Quality Mgmt",   sub: "SPC · NCR · CAPA",       color: T.pink },
    { x: 840, label: "EasyPlan",       sub: "Process eng",            color: T.gold },
  ];
  // L2 gateways
  const L2_GW = [
    { x: 240, label: "Edge GW",        handles: ["EQUIPMENT"], color: T.teal },
    { x: 540, label: "Confirm GW",     handles: ["OPERATIONS_PERFORMANCE"], color: T.orange },
  ];
  // L2 systems
  const L2_SYS = [
    { x: 240, label: "Industrial Edge",sub: "OPC-UA · alarms",        color: T.teal },
    { x: 540, label: "SFC terminals",  sub: "Operator UI",            color: T.orange },
    { x: 840, label: "SCADA",          sub: "Process visibility",     color: T.steel },
  ];
  // L1 devices
  const L1_DEV = [
    { x: 180, label: "PLCs",           sub: "S7 / others",            color: T.steel },
    { x: 380, label: "CNC",            sub: "Sinumerik / Fanuc",      color: T.steel },
    { x: 580, label: "Robots",         sub: "6-axis · cobot",         color: T.purple },
    { x: 780, label: "IIoT sensors",   sub: "Vibration · temp",       color: T.teal },
  ];
  // L0
  const L0_PHY = [
    { x: 380, label: "Conveyors / AGV",sub: "Material flow",           color: T.steel },
    { x: 680, label: "Production process", sub: "Forming · joining",   color: T.steel },
  ];

  return (
    <div style={{
      background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
      padding: 14, overflow: "auto",
    }}>
      <div style={{ fontSize: 10, color: T.text2, marginBottom: 8, fontStyle: "italic" }}>
        Each ISA-95 level is a swim lane. Bus segments run between levels. Gateways sit ON the bus and own
        the B2MML serialisation in/out of their tier. Click any element for detail.
      </div>

      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", background: T.bg }}>
        <defs>
          <linearGradient id="bus-grad-ent" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor={T.green} stopOpacity="0.1"/>
            <stop offset="50%" stopColor={T.green} stopOpacity="0.4"/>
            <stop offset="100%" stopColor={T.green} stopOpacity="0.1"/>
          </linearGradient>
          <linearGradient id="bus-grad-plant" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor={T.cyan} stopOpacity="0.1"/>
            <stop offset="50%" stopColor={T.cyan} stopOpacity="0.4"/>
            <stop offset="100%" stopColor={T.cyan} stopOpacity="0.1"/>
          </linearGradient>
          <linearGradient id="bus-grad-field" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"  stopColor={T.text3} stopOpacity="0.05"/>
            <stop offset="50%" stopColor={T.text3} stopOpacity="0.3"/>
            <stop offset="100%" stopColor={T.text3} stopOpacity="0.05"/>
          </linearGradient>
          <marker id="b2mml-arr" viewBox="0 0 10 10" refX="9" refY="5"
            markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 Z" fill={T.cyan}/>
          </marker>
          <filter id="b2mml-glow">
            <feGaussianBlur stdDeviation="2.5" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>

        {/* ─── Swim-lane labels (left edge) ─── */}
        {ISA95_LEVELS.map((lvl, i) => (
          <g key={lvl.id}>
            <rect x={0} y={70 + LANE_H * i} width={W} height={LANE_H - 2}
              fill={`${lvl.color}07`} stroke={`${lvl.color}25`} strokeWidth="1" strokeDasharray="3,3"/>
            <rect x={20} y={70 + LANE_H * i + 12} width={60} height={26} rx={4}
              fill={`${lvl.color}30`} stroke={`${lvl.color}90`}/>
            <text x={50} y={70 + LANE_H * i + 30} fontSize="13" fontWeight="700"
              fill={lvl.color} textAnchor="middle" fontFamily={FONTS.mono}>
              {lvl.id}
            </text>
            <text x={92} y={70 + LANE_H * i + 25} fontSize="11" fill={lvl.color}
              fontWeight="700" letterSpacing="1" fontFamily={FONTS.mono}>
              {lvl.name.toUpperCase()}
            </text>
            <text x={92} y={70 + LANE_H * i + 40} fontSize="8.5" fill={T.text2}
              fontStyle="italic">
              {lvl.systems.join(" · ")}
            </text>
          </g>
        ))}

        {/* ─── Enterprise bus (between L4 and L3) ─── */}
        <BusRail y={ENT_BUS_Y} label="ENTERPRISE BUS · Kafka · Schema Registry · mTLS"
          sublabel="Topics: personnel.v1 · equipment.v1 · material.{def,lot}.v1 · ops.{schedule,performance,capability}.v1"
          gradId="bus-grad-ent" color={T.green} W={W}/>
        {/* ─── Plant bus (between L3 and L2) ─── */}
        <BusRail y={PLANT_BUS_Y} label="PLANT BUS · per-plant · OPC-UA + Kafka"
          sublabel="Topics: ops.definition.v1 · ops.dispatch.v1 · equipment.{state,alarm}.v1 · material.lot.dispatch.v1"
          gradId="bus-grad-plant" color={T.cyan} W={W}/>
        {/* ─── Fieldbus (below L2) ─── */}
        <BusRail y={FIELD_BUS_Y} label="FIELDBUS · PROFINET / EtherCAT / EtherNet/IP · deterministic"
          sublabel="Below B2MML — hardware-mapped data; safety bus separated"
          gradId="bus-grad-field" color={T.text3} W={W} dim/>

        {/* ─── L4 systems ─── */}
        {L4_SYS.map((sys, i) => (
          <SystemNode key={`L4S-${i}`} x={sys.x} y={laneY(0) - 20} {...sys}/>
        ))}
        {/* L4 gateways on enterprise bus */}
        {L4_GW.map((g, i) => (
          <GatewayChip key={`L4G-${i}`} x={g.x} y={ENT_BUS_Y} {...g} onClick={(schemaId) => { setSelectedSchema(schemaId); setView("schemas"); }}/>
        ))}

        {/* L3 ingress / egress on enterprise bus */}
        {L3_GW.slice(0, 2).map((g, i) => (
          <GatewayChip key={`L3G-${i}`} x={g.x} y={ENT_BUS_Y + 22} {...g} flip
            onClick={(schemaId) => { setSelectedSchema(schemaId); setView("schemas"); }}/>
        ))}
        {/* L3 systems */}
        {L3_SYS.map((sys, i) => (
          <SystemNode key={`L3S-${i}`} x={sys.x} y={laneY(1) - 10} {...sys}/>
        ))}
        {/* L3 dispatch GW on plant bus */}
        <GatewayChip x={L3_GW[2].x} y={PLANT_BUS_Y} {...L3_GW[2]} onClick={(schemaId) => { setSelectedSchema(schemaId); setView("schemas"); }}/>

        {/* L2 gateways on plant bus */}
        {L2_GW.map((g, i) => (
          <GatewayChip key={`L2G-${i}`} x={g.x} y={PLANT_BUS_Y + 22} {...g} flip
            onClick={(schemaId) => { setSelectedSchema(schemaId); setView("schemas"); }}/>
        ))}
        {/* L2 systems */}
        {L2_SYS.map((sys, i) => (
          <SystemNode key={`L2S-${i}`} x={sys.x} y={laneY(2) - 10} {...sys}/>
        ))}

        {/* L1 devices */}
        {L1_DEV.map((d, i) => (
          <SystemNode key={`L1D-${i}`} x={d.x} y={laneY(3) - 10} {...d} small/>
        ))}

        {/* L0 physical */}
        {L0_PHY.map((p, i) => (
          <SystemNode key={`L0P-${i}`} x={p.x} y={laneY(4) - 10} {...p} physical/>
        ))}

        {/* ─── Vertical edges: L4 system → its gateway → bus ─── */}
        {L4_SYS.map((sys, i) => (
          <line key={`L4-down-${i}`} x1={sys.x} y1={laneY(0) + 8}
            x2={sys.x} y2={ENT_BUS_Y - 16}
            stroke={sys.color} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7"/>
        ))}
        {/* L3 systems up to bus + down to plant bus */}
        {L3_SYS.map((sys, i) => (
          <g key={`L3-conn-${i}`}>
            <line x1={sys.x} y1={laneY(1) - 22}
              x2={sys.x} y2={ENT_BUS_Y + 18}
              stroke={sys.color} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7"/>
            <line x1={sys.x} y1={laneY(1) + 22}
              x2={sys.x} y2={PLANT_BUS_Y - 16}
              stroke={sys.color} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7"/>
          </g>
        ))}
        {/* L2 systems up to plant bus + down to fieldbus */}
        {L2_SYS.map((sys, i) => (
          <g key={`L2-conn-${i}`}>
            <line x1={sys.x} y1={laneY(2) - 22}
              x2={sys.x} y2={PLANT_BUS_Y + 18}
              stroke={sys.color} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7"/>
            <line x1={sys.x} y1={laneY(2) + 22}
              x2={sys.x} y2={FIELD_BUS_Y - 12}
              stroke={sys.color} strokeWidth="1" strokeDasharray="2,2" opacity="0.4"/>
          </g>
        ))}
        {/* L1 devices up to fieldbus */}
        {L1_DEV.map((d, i) => (
          <line key={`L1-up-${i}`} x1={d.x} y1={laneY(3) - 18}
            x2={d.x} y2={FIELD_BUS_Y + 8}
            stroke={d.color} strokeWidth="1" strokeDasharray="2,2" opacity="0.4"/>
        ))}
        {/* L1 -> L0 physical */}
        {L0_PHY.map((p, i) => (
          <line key={`L0-up-${i}`} x1={p.x} y1={laneY(4) - 18}
            x2={p.x} y2={laneY(3) + 22}
            stroke="#475569" strokeWidth="1" strokeDasharray="2,2" opacity="0.3"/>
        ))}

        {/* Animated message dot circling the enterprise bus */}
        <circle r="3.5" fill={T.cyan} filter="url(#b2mml-glow)">
          <animateMotion dur="6s" repeatCount="indefinite"
            path={`M 140 ${ENT_BUS_Y} L ${W - 100} ${ENT_BUS_Y}`}/>
        </circle>
        <circle r="3" fill={T.green} filter="url(#b2mml-glow)">
          <animateMotion dur="8s" repeatCount="indefinite" begin="2s"
            path={`M ${W - 100} ${ENT_BUS_Y} L 140 ${ENT_BUS_Y}`}/>
        </circle>
        <circle r="3" fill={T.cyan} filter="url(#b2mml-glow)">
          <animateMotion dur="5s" repeatCount="indefinite" begin="1s"
            path={`M 140 ${PLANT_BUS_Y} L ${W - 200} ${PLANT_BUS_Y}`}/>
        </circle>
      </svg>

      {/* Legend below map */}
      <div style={{
        marginTop: 12, padding: "10px 14px", background: T.bg3,
        border: `1px solid ${T.border}`, borderRadius: 6,
      }}>
        <div style={{ fontSize: 9, color: T.cyan, fontFamily: FONTS.mono, letterSpacing: 1, marginBottom: 6 }}>
          KEY · how to read this diagram
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8, fontSize: 9.5, color: T.text2 }}>
          <div><strong style={{ color: T.green }}>Enterprise bus</strong> — Kafka, schema-registered. Carries
            OperationsSchedule + Performance + Material + Personnel + Capability between L4 and L3.</div>
          <div><strong style={{ color: T.cyan }}>Plant bus</strong> — local Kafka or RabbitMQ. Carries
            OperationsDefinition (recipes) and MaterialLot dispatch + Equipment state from L3 to L2.</div>
          <div><strong style={{ color: T.text2 }}>Fieldbus</strong> — deterministic, hardware-mapped.
            BELOW B2MML — control loops in microseconds, not XML.</div>
          <div><strong style={{ color: T.amber }}>Gateways</strong> — sit ON the bus, do B2MML serialisation
            in and out of their tier. They are the ONLY conduit across federation boundaries.</div>
        </div>
      </div>
    </div>
  );
}

function BusRail({ y, label, sublabel, gradId, color, W, dim }) {
  return (
    <g opacity={dim ? 0.5 : 1}>
      <rect x={20} y={y - 8} width={W - 40} height={16} rx={8}
        fill={`url(#${gradId})`} stroke={color} strokeWidth="1.5"
        strokeDasharray={dim ? "4,3" : "none"}/>
      <text x={W / 2} y={y - 14} textAnchor="middle"
        fontSize="9.5" fontFamily={FONTS.mono} fontWeight="700"
        fill={color} letterSpacing="1.5">
        {label}
      </text>
      <text x={W / 2} y={y + 18} textAnchor="middle"
        fontSize="8" fill={T.text3} fontStyle="italic">
        {sublabel}
      </text>
    </g>
  );
}

function SystemNode({ x, y, label, sub, color, small, physical }) {
  const w = small ? 90 : 130;
  const h = small ? 36 : 44;
  return (
    <g transform={`translate(${x - w/2}, ${y - h/2})`}>
      {physical ? (
        <polygon points={`12,0 ${w-12},0 ${w},${h} 0,${h}`}
          fill={`${color}15`} stroke={color} strokeWidth="1.5"/>
      ) : (
        <rect x={0} y={0} width={w} height={h} rx={6}
          fill={`${color}15`} stroke={color} strokeWidth="1.5"/>
      )}
      <text x={w/2} y={h/2 - 2} textAnchor="middle"
        fontSize={small ? "9" : "10.5"} fontWeight="700"
        fill={T.text}>{label}</text>
      <text x={w/2} y={h/2 + 11} textAnchor="middle"
        fontSize="7.5" fill={T.text2} fontStyle="italic">{sub}</text>
    </g>
  );
}

function GatewayChip({ x, y, label, handles, color, flip, onClick }) {
  const w = 130;
  const h = 22;
  // Hexagon points
  const inset = 8;
  const pts = `${inset},0 ${w-inset},0 ${w},${h/2} ${w-inset},${h} ${inset},${h} 0,${h/2}`;
  const clickable = !!onClick && handles?.length > 0;

  return (
    <g transform={`translate(${x - w/2}, ${y - h/2 + (flip ? 0 : 0)})`}
       onClick={() => clickable && onClick(handles[0])}
       style={{ cursor: clickable ? "pointer" : "default" }}>
      <polygon points={pts}
        fill={`${color}30`} stroke={color} strokeWidth="1.5"/>
      <text x={w/2} y={h/2 + 4} textAnchor="middle"
        fontSize="9" fontWeight="700"
        fill={color} fontFamily={FONTS.mono}>
        {label}
      </text>
      {/* Tiny schema badges under the chip */}
      {handles?.length > 0 && (
        <text x={w/2} y={h + 11} textAnchor="middle"
          fontSize="6.5" fill={T.text3} fontFamily={FONTS.mono}>
          {handles.map(h => schemaById(h)?.icon).filter(Boolean).join(" ")}
        </text>
      )}
    </g>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2 · SCHEMAS VIEW
// ═══════════════════════════════════════════════════════════════════════════
function SchemasView({ selectedSchema, setSelectedSchema, setView, setSelectedPayload }) {
  const selected = selectedSchema ? schemaById(selectedSchema) : null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 14 }}>
      {/* Schema list */}
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        maxHeight: 700, overflowY: "auto",
      }}>
        {B2MML_SCHEMAS.map(s => {
          const isSel = selectedSchema === s.id;
          return (
            <div key={s.id} onClick={() => setSelectedSchema(s.id)}
              style={{
                padding: "10px 14px", cursor: "pointer",
                borderBottom: `1px solid ${T.border2}`,
                borderLeft: `3px solid ${isSel ? s.color : "transparent"}`,
                background: isSel ? `${s.color}10` : "transparent",
              }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 16 }}>{s.icon}</span>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, color: s.color }}>
                    {s.name}
                  </span>
                </span>
                <span style={{ fontSize: 7, color: T.text3, fontFamily: FONTS.mono }}>{s.isaPart}</span>
              </div>
              <div style={{ fontSize: 9, color: T.text2 }}>{s.purpose}</div>
            </div>
          );
        })}
      </div>

      {/* Detail pane */}
      {selected ? (
        <SchemaDetail schema={selected} setView={setView} setSelectedPayload={setSelectedPayload}/>
      ) : (
        <div style={{
          background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
          padding: 40, textAlign: "center", color: T.text3, fontStyle: "italic",
        }}>
          ← Select a B2MML schema to see its purpose, key classes, federation rule, and XML samples.
        </div>
      )}
    </div>
  );
}

function SchemaDetail({ schema, setView, setSelectedPayload }) {
  const contracts = contractsBySchema(schema.id);
  const payloads = payloadsBySchema(schema.id);

  return (
    <div style={{
      background: T.bg2, border: `1px solid ${schema.color}40`, borderTop: `4px solid ${schema.color}`,
      borderRadius: 8, padding: "16px 20px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 30 }}>{schema.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: schema.color }}>
            {schema.name}
          </div>
          <div style={{ fontSize: 9, color: T.text3, fontFamily: FONTS.mono }}>
            {schema.isaPart} · {schema.namespace}
          </div>
        </div>
        {schema.bidirectional && <span style={pill(T.blue)}>↔ bidirectional</span>}
      </div>

      <Field label="🎯 Purpose"          value={schema.purpose} color={schema.color}/>
      <Field label="🏠 Master data at"   value={schema.masterDataAt} color={T.amber}/>
      <Field label="🤝 Federation rule"  value={schema.federationRule} color={T.green}/>

      <div style={{
        background: T.bg3, border: `1px solid ${schema.color}30`,
        borderRadius: 6, padding: "10px 12px", marginBottom: 10,
      }}>
        <div style={{ fontSize: 8, color: schema.color, letterSpacing: 1, fontWeight: 700,
                      textTransform: "uppercase", marginBottom: 6 }}>
          🔑 Key ISA-95 classes ({schema.keyClasses.length})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {schema.keyClasses.map(c => (
            <span key={c} style={{
              fontSize: 8.5, fontFamily: FONTS.mono, color: schema.color,
              padding: "2px 7px", background: `${schema.color}15`,
              border: `1px solid ${schema.color}30`, borderRadius: 4,
            }}>{c}</span>
          ))}
        </div>
      </div>

      <div style={{
        background: T.bg3, border: `1px solid ${T.cyan}30`,
        borderRadius: 6, padding: "10px 12px", marginBottom: 10,
      }}>
        <div style={{ fontSize: 8, color: T.cyan, letterSpacing: 1, fontWeight: 700,
                      textTransform: "uppercase", marginBottom: 6 }}>
          ⚙️ Transaction types ({schema.transactionTypes.length})
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {schema.transactionTypes.map(t => (
            <span key={t} style={{
              fontSize: 9, fontFamily: FONTS.mono, color: T.cyan, fontWeight: 700,
              padding: "2px 8px", background: `${T.cyan}15`,
              border: `1px solid ${T.cyan}40`, borderRadius: 4,
            }}>{t}</span>
          ))}
        </div>
      </div>

      {/* Contracts */}
      <div style={{
        background: T.bg3, border: `1px solid ${T.green}30`,
        borderRadius: 6, padding: "10px 12px", marginBottom: 10,
      }}>
        <div style={{ fontSize: 8, color: T.green, letterSpacing: 1, fontWeight: 700,
                      textTransform: "uppercase", marginBottom: 6 }}>
          🤝 Federation contracts using this schema ({contracts.length})
        </div>
        {contracts.length === 0 ? (
          <div style={{ fontSize: 9, color: T.text3, fontStyle: "italic" }}>(none — internal schema)</div>
        ) : (
          contracts.map(c => (
            <div key={c.id} style={{ fontSize: 9, color: T.text2, marginBottom: 4 }}>
              <span style={{ color: T.green, fontFamily: FONTS.mono }}>
                {c.fromLevel} {c.direction === "bidirectional" ? "↔" : "→"} {c.toLevel}
              </span>
              {" "}· <span style={{ color: T.text }}>{c.purpose}</span>
            </div>
          ))
        )}
      </div>

      {/* Sample payloads */}
      {payloads.length > 0 && (
        <div style={{
          background: `${T.amber}10`, border: `1px solid ${T.amber}30`,
          borderRadius: 6, padding: "10px 12px", marginBottom: 10,
        }}>
          <div style={{ fontSize: 8, color: T.amber, letterSpacing: 1, fontWeight: 700,
                        textTransform: "uppercase", marginBottom: 6 }}>
            📨 Sample XML payloads ({payloads.length})
          </div>
          {payloads.map(p => (
            <button key={p.id}
              onClick={() => { setSelectedPayload(p.id); setView("payloads"); }}
              style={{
                display: "block", width: "100%", textAlign: "left",
                background: T.bg2, border: `1px solid ${T.amber}40`,
                color: T.amber, padding: "6px 10px", borderRadius: 4,
                fontSize: 9, fontFamily: FONTS.mono, marginBottom: 4,
                cursor: "pointer",
              }}>
              {p.title} →
            </button>
          ))}
        </div>
      )}

      {/* Linked CLM interfaces (cross-tab nav into LLD) */}
      {(() => {
        const linked = interfacesForSchema(schema.id);
        if (linked.length === 0) return null;
        return (
          <div style={{
            background: `${T.cyan}10`, border: `1px solid ${T.cyan}30`,
            borderRadius: 6, padding: "10px 12px",
          }}>
            <EntityRefList kind="interface" ids={linked}
              label="🔗 CLM interfaces using this schema (click → LLD)" />
            <div style={{ fontSize: 8, color: T.text3, fontStyle: "italic", marginTop: 6 }}>
              These project interfaces produce or consume {schema.name} payloads on the bus.
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 3 · CONTRACTS VIEW
// ═══════════════════════════════════════════════════════════════════════════
function ContractsView({ setView, setSelectedPayload }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 12 }}>
      {FEDERATION_CONTRACTS.map(c => {
        const isTier1 = c.semantic === "exactly-once";
        const isNonB2MML = c.nonB2MML;
        const baseColor = isNonB2MML ? T.text3 : (isTier1 ? T.red : T.green);
        // First example payload from the first schema (for "see sample" link)
        const exampleId = c.schemas?.flatMap(s => schemaById(s)?.examplePayloadIds || [])[0];
        return (
          <div key={c.id} style={{
            background: T.bg2, border: `1px solid ${baseColor}40`,
            borderLeft: `4px solid ${baseColor}`, borderRadius: 6, padding: "12px 14px",
            opacity: isNonB2MML ? 0.7 : 1,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 8, color: T.text3 }}>{c.id}</span>
              <span style={{
                fontFamily: FONTS.mono, fontSize: 11, fontWeight: 700, color: baseColor,
                padding: "2px 8px", background: `${baseColor}15`, borderRadius: 4,
              }}>
                {c.fromLevel} {c.direction === "bidirectional" ? "↔" : c.direction === "down" ? "↓" : "↑"} {c.toLevel}
              </span>
              {isTier1 && <span style={pill(T.red)}>TIER-1 · exactly-once</span>}
              {isNonB2MML && <span style={pill(T.text3)}>non-B2MML</span>}
            </div>

            <div style={{ fontSize: 10.5, color: T.text, marginBottom: 8, lineHeight: 1.5 }}>
              {c.purpose}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: "4px 10px", fontSize: 9 }}>
              <div style={{ color: T.text3, fontFamily: FONTS.mono }}>SCHEMAS</div>
              <div style={{ color: T.text2 }}>
                {c.schemas?.length > 0 ? c.schemas.map(s => (
                  <span key={s} style={{
                    fontSize: 8, fontFamily: FONTS.mono,
                    color: schemaById(s)?.color || T.text2,
                    padding: "1px 6px", background: `${schemaById(s)?.color || T.text3}15`,
                    border: `1px solid ${schemaById(s)?.color || T.text3}40`,
                    borderRadius: 3, marginRight: 4,
                  }}>{schemaById(s)?.icon} {s}</span>
                )) : <span style={{ color: T.text3, fontStyle: "italic" }}>(no B2MML — fieldbus)</span>}
              </div>
              {c.transactions?.length > 0 && <>
                <div style={{ color: T.text3, fontFamily: FONTS.mono }}>TRANSACTIONS</div>
                <div style={{ color: T.cyan, fontFamily: FONTS.mono }}>{c.transactions.join(" · ")}</div>
              </>}
              <div style={{ color: T.text3, fontFamily: FONTS.mono }}>SEMANTIC</div>
              <div style={{ color: isTier1 ? T.red : T.amber, fontFamily: FONTS.mono, fontWeight: 700 }}>
                {c.semantic}
              </div>
              <div style={{ color: T.text3, fontFamily: FONTS.mono }}>SLA</div>
              <div style={{ color: T.green, fontFamily: FONTS.mono }}>{c.sla}</div>
              <div style={{ color: T.text3, fontFamily: FONTS.mono }}>RATE OF CHANGE</div>
              <div style={{ color: T.text2, fontStyle: "italic" }}>{c.rateOfChange}</div>
              <div style={{ color: T.text3, fontFamily: FONTS.mono }}>BUS</div>
              <div style={{ color: T.text }}>{c.bus}</div>
              <div style={{ color: T.text3, fontFamily: FONTS.mono }}>FAILURE MODE</div>
              <div style={{ color: T.amber, fontStyle: "italic" }}>{c.failureMode}</div>
            </div>

            {/* Cross-tab navigation buttons */}
            {(exampleId || c.schemas?.length > 0) && setView && (
              <div style={{
                display: "flex", gap: 6, marginTop: 10, paddingTop: 8,
                borderTop: `1px solid ${baseColor}25`,
              }}>
                {exampleId && (
                  <button onClick={() => {
                    if (setSelectedPayload) setSelectedPayload(exampleId);
                    setView("payloads");
                  }} style={{
                    fontSize: 9, padding: "3px 9px", borderRadius: 3,
                    background: `${T.amber}15`, color: T.amber,
                    border: `1px solid ${T.amber}50`, cursor: "pointer",
                    fontFamily: FONTS.mono,
                  }}>📨 see sample XML</button>
                )}
                {c.schemas?.[0] && (
                  <button onClick={() => setView("schemas")} style={{
                    fontSize: 9, padding: "3px 9px", borderRadius: 3,
                    background: `${T.purple}15`, color: T.purple,
                    border: `1px solid ${T.purple}50`, cursor: "pointer",
                    fontFamily: FONTS.mono,
                  }}>📚 open schema</button>
                )}
              </div>
            )}

            {/* Linked CLM interfaces — cross-tab nav into LLD */}
            {(() => {
              const linked = [...new Set(c.schemas?.flatMap(s => interfacesForSchema(s)) || [])];
              if (linked.length === 0) return null;
              return (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${baseColor}25` }}>
                  <EntityRefList kind="interface" ids={linked}
                    label="🔗 CLM interfaces carrying this contract"/>
                </div>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 4 · PAYLOADS VIEW
// ═══════════════════════════════════════════════════════════════════════════
function PayloadsView({ selectedPayload, setSelectedPayload }) {
  const payload = payloadById(selectedPayload) || Object.values(XML_PAYLOADS)[0];
  const allIds = Object.keys(XML_PAYLOADS);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 14 }}>
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        maxHeight: 700, overflowY: "auto",
      }}>
        {allIds.map(id => {
          const p = XML_PAYLOADS[id];
          const isSel = selectedPayload === id;
          const schemaColor = schemaById(p.schema)?.color || T.text2;
          return (
            <div key={id} onClick={() => setSelectedPayload(id)}
              style={{
                padding: "10px 14px", cursor: "pointer",
                borderBottom: `1px solid ${T.border2}`,
                borderLeft: `3px solid ${isSel ? schemaColor : "transparent"}`,
                background: isSel ? `${schemaColor}10` : "transparent",
              }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontFamily: FONTS.mono, fontSize: 9, color: schemaColor, fontWeight: 700 }}>
                  {p.schema}
                </span>
                <span style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono }}>
                  {p.transaction}
                </span>
              </div>
              <div style={{ fontSize: 10, color: isSel ? T.text : T.text2, marginBottom: 3 }}>
                {p.title}
              </div>
              <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono }}>
                {p.direction}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: 700,
      }}>
        <div style={{
          padding: "12px 16px", borderBottom: `1px solid ${T.border}`, background: T.bg3,
        }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
            <span style={{ ...pill(schemaById(payload.schema)?.color || T.cyan) }}>
              {schemaById(payload.schema)?.icon} {payload.schema}
            </span>
            <span style={pill(T.amber)}>{payload.transaction}</span>
            <span style={{
              fontSize: 9, fontFamily: FONTS.mono, color: T.green,
              padding: "2px 8px", background: `${T.green}15`, borderRadius: 4,
            }}>
              {payload.direction}
            </span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: FONTS.header }}>
            {payload.title}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          <pre style={{
            background: T.bg3, border: `1px solid ${T.border}`, borderRadius: 4,
            padding: "12px 14px", fontSize: 10, color: T.text,
            fontFamily: "'Fira Code', 'Cascadia Code', Consolas, monospace",
            lineHeight: 1.5, overflowX: "auto", margin: 0,
            whiteSpace: "pre-wrap", wordBreak: "break-all",
          }}>{highlightXML(payload.xml)}</pre>

          <div style={{
            marginTop: 12, padding: "10px 14px",
            background: `${T.amber}10`, border: `1px solid ${T.amber}30`,
            borderRadius: 6,
          }}>
            <div style={{ fontSize: 9, color: T.amber, fontFamily: FONTS.mono, letterSpacing: 1, marginBottom: 6 }}>
              📝 ANNOTATION NOTES
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 9.5, color: T.text2, lineHeight: 1.6 }}>
              {payload.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simple XML syntax highlighter via JSX
function highlightXML(xml) {
  const parts = [];
  let lastIdx = 0;
  const re = /(<\/?[^>]+>)/g;
  let m;
  let key = 0;
  while ((m = re.exec(xml)) !== null) {
    if (m.index > lastIdx) {
      parts.push(<span key={key++} style={{ color: T.text }}>{xml.slice(lastIdx, m.index)}</span>);
    }
    parts.push(<span key={key++} style={{ color: T.cyan }}>{m[0]}</span>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < xml.length) {
    parts.push(<span key={key++} style={{ color: T.text }}>{xml.slice(lastIdx)}</span>);
  }
  return parts;
}

// ═══════════════════════════════════════════════════════════════════════════
// 5 · GATEWAYS VIEW
// ═══════════════════════════════════════════════════════════════════════════
function GatewaysView() {
  return (
    <div>
      {BUS_TOPOLOGY.segments.map(seg => (
        <div key={seg.id} style={{ marginBottom: 22 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 10, marginBottom: 10,
            padding: "10px 14px", background: T.bg2,
            borderLeft: `4px solid ${seg.nonB2MML ? T.text3 : T.green}`, borderRadius: 4,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700,
                            color: seg.nonB2MML ? T.text3 : T.green }}>
                {seg.label} · {seg.level}
              </div>
              <div style={{ fontSize: 9, color: T.text2, fontStyle: "italic" }}>
                {seg.tech} · {seg.registry} · {seg.auth} · {seg.slaTier}
              </div>
            </div>
          </div>

          {/* Topics on this segment */}
          {seg.topics?.length > 0 && (
            <div style={{ marginLeft: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono,
                            letterSpacing: 1, marginBottom: 6 }}>
                TOPICS ON THIS SEGMENT
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 6 }}>
                {seg.topics.map(t => {
                  const sCol = schemaById(t.schema)?.color || T.cyan;
                  return (
                    <div key={t.name} style={{
                      background: T.bg3, border: `1px solid ${sCol}30`,
                      borderRadius: 4, padding: "6px 10px", fontSize: 9,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                        <span style={{ color: sCol, fontFamily: FONTS.mono, fontWeight: 700 }}>{t.name}</span>
                        <span style={{ color: T.text3, fontFamily: FONTS.mono, fontSize: 7.5 }}>
                          ret {t.retention} · {t.partitions || "n/a"} part
                        </span>
                      </div>
                      <div style={{ fontSize: 8.5, color: T.text2 }}>
                        {schemaById(t.schema)?.icon} {t.schema}
                        {t.type && <span style={{ color: T.amber, marginLeft: 6 }}>· {t.type}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Gateways on this segment */}
          {(() => {
            const gws = BUS_TOPOLOGY.gateways.filter(g => g.segment === seg.id);
            if (gws.length === 0) return null;
            return (
              <div style={{ marginLeft: 14 }}>
                <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono,
                              letterSpacing: 1, marginBottom: 6 }}>
                  GATEWAYS ATTACHED TO THIS SEGMENT
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 8 }}>
                  {gws.map(g => (
                    <div key={g.id} style={{
                      background: T.bg2, border: `1px solid ${T.amber}40`,
                      borderTop: `3px solid ${T.amber}`,
                      borderRadius: 6, padding: "10px 12px",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, color: T.amber }}>
                          {g.label}
                        </span>
                        <span style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono }}>{g.level}</span>
                      </div>
                      <div style={{ fontSize: 9, color: T.text2, marginBottom: 6, lineHeight: 1.5 }}>
                        {g.role}
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>
                        {g.handles.map(h => (
                          <span key={h} style={{
                            fontSize: 7.5, fontFamily: FONTS.mono,
                            color: schemaById(h)?.color || T.text2,
                            padding: "1px 5px",
                            background: `${schemaById(h)?.color || T.text3}15`,
                            border: `1px solid ${schemaById(h)?.color || T.text3}40`,
                            borderRadius: 3,
                          }}>
                            {schemaById(h)?.icon} {h}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: 8, color: T.text3, fontFamily: FONTS.mono, fontStyle: "italic" }}>
                        ⚙ {g.tech}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 6 · CONFORMANCE VIEW
// ═══════════════════════════════════════════════════════════════════════════
function ConformanceView() {
  const checks = [
    { num: 1, title: "Does the flow cross an ISA-95 level boundary?",
      detail: "If NO — keep it inside the tier with native APIs (faster, simpler). If YES — proceed.",
      color: T.cyan },
    { num: 2, title: "Identify the canonical ISA-95 object",
      detail: "Personnel · Equipment · Material · ProcessSegment · OperationsDefinition · OperationsSchedule · OperationsPerformance · OperationsCapability.",
      color: T.purple },
    { num: 3, title: "Use the existing B2MML XSD — do not extend",
      detail: "All extensions go in *Property elements with ID + Value + UnitOfMeasure. Adding new root elements breaks federation.",
      color: T.amber },
    { num: 4, title: "Choose the right transaction verb",
      detail: "Get (sync request) · Show (notify of state) · Process (please act) · Change (modify) · Cancel (revoke). Tier-1 flows must use Process+Show pairs.",
      color: T.green },
    { num: 5, title: "Specify delivery semantics",
      detail: "at-most-once for telemetry · at-least-once for everything else · exactly-once for financial actuals. Idempotency key MUST be the ID from the XML.",
      color: T.red },
    { num: 6, title: "Pick the bus segment",
      detail: "Enterprise bus for L4↔L3 · Plant bus for L3↔L2 · NEVER cross-segment direct. Each tier publishes/consumes through ITS gateway.",
      color: T.blue },
    { num: 7, title: "Register the schema version",
      detail: "Even if you're using a standard B2MML schema, register YOUR version with the schema registry. BACKWARD compatibility mode default.",
      color: T.teal },
    { num: 8, title: "Define the failure mode explicitly",
      detail: "What happens to the sender if no ACK in T seconds? Buffer? Retry? DLQ? Drop? No undefined behavior at the federation boundary.",
      color: T.pink },
    { num: 9, title: "Write the federation contract",
      detail: "One markdown file. Schemas + transactions + semantic + SLA + rate-of-change + bus + failure mode + version. Reviewed by both tier owners.",
      color: T.gold },
    { num: 10, title: "Conformance test the wire",
      detail: "Generate sample XML in the schema, validate against XSD, run through the receiver's ingress. Test ALL transactions, not just the happy path.",
      color: T.cyan },
  ];

  return (
    <div>
      <div style={{
        padding: "14px 18px", marginBottom: 16,
        background: `${T.amber}10`, border: `1px solid ${T.amber}40`, borderRadius: 6,
        fontSize: 10, color: T.amber,
      }}>
        🎯 <strong>The 10-step B2MML conformance checklist.</strong> Run this for every new federation flow.
        Any "no" on steps 1–9 means you don't have B2MML conformance — fix it before step 10. A passing step 10
        is the gate to production.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
        {checks.map(c => (
          <div key={c.num} style={{
            background: T.bg2, border: `1px solid ${c.color}30`,
            borderLeft: `4px solid ${c.color}`, borderRadius: 6, padding: "10px 14px",
            display: "flex", gap: 14, alignItems: "flex-start",
          }}>
            <div style={{
              fontFamily: FONTS.header, fontSize: 22, fontWeight: 700,
              color: c.color, opacity: 0.55, minWidth: 32, textAlign: "center",
            }}>
              {String(c.num).padStart(2, "0")}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 4 }}>
                {c.title}
              </div>
              <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.5 }}>
                {c.detail}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 18, padding: "12px 16px",
        background: T.bg2, border: `1px solid ${T.green}30`, borderRadius: 6,
      }}>
        <div style={{ fontSize: 11, color: T.green, fontFamily: FONTS.mono, fontWeight: 700,
                      letterSpacing: 1, marginBottom: 8 }}>
          ✓ FEDERATION HEALTH SIGNAL
        </div>
        <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.6 }}>
          When B2MML federation is working, you can answer YES to all four:
          <br/>· Can a tier owner change their internal system <strong style={{ color: T.green }}>without breaking another tier</strong>?
          <br/>· Can you trace a transaction <strong style={{ color: T.green }}>end-to-end via correlation IDs in the XML</strong>?
          <br/>· Can you replay any tier-1 message <strong style={{ color: T.green }}>from the DLQ without side effects</strong>?
          <br/>· Can a new auditor read the XML and understand the business action <strong style={{ color: T.green }}>without reading code</strong>?
        </div>
      </div>
    </div>
  );
}

// ─── helper field component ──────────────────────────────────────────
function Field({ label, value, color }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 8, color, letterSpacing: 1, fontWeight: 700,
                    textTransform: "uppercase", marginBottom: 3 }}>
        {label}
      </div>
      <div style={{ fontSize: 10.5, color: T.text2, lineHeight: 1.5 }}>{value}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LiveB2MMLFlow · animated B2MML pipeline (producer → XSD → registry → bus → consumer)
// ═══════════════════════════════════════════════════════════════════════════
// What stakeholders see:
//   1. Producer (e.g., SAP) emits a B2MML envelope
//   2. Gateway serializes to canonical XML, computes idempotency key from <ID>
//   3. XSD validation gate (pass/fail visible)
//   4. Schema registry version check
//   5. Topic write with replication factor + min.insync.replicas
//   6. Consumer poll
//   7. Idempotency check
//   8. Translated to local model, applied to target system
//
// Toggle a "schema drift" failure mode to show what happens when the
// producer publishes an incompatible XSD version. This is the single
// most common B2MML failure in practice.
// ═══════════════════════════════════════════════════════════════════════════

function LiveB2MMLFlow({ setView, setSelectedPayload }) {
  const [scenario, setScenario] = useState("ops-schedule");
  const [showFailure, setShowFailure] = useState(false);
  const [speed, setSpeed] = useState("normal");

  // Map of scenario → flow definition
  const SCENARIOS = {
    "ops-schedule": {
      label: "OperationsSchedule release",
      from: "SAP ERP", to: "Opcenter EX",
      schema: "OPERATIONS_SCHEDULE",
      payloadId: "XML-OPSCHED-001",
      semantic: "exactly-once",
      sla: "P99 < 30s",
      color: T.green,
      desc: "ERP releases a work order. Tier-1 critical: production cannot start without this.",
    },
    "material-lot": {
      label: "MaterialLot consumption",
      from: "Opcenter EX", to: "SAP ERP",
      schema: "MATERIAL",
      payloadId: "XML-MATERIAL-002",
      semantic: "at-least-once",
      sla: "P99 < 30s",
      color: T.gold,
      desc: "Operator consumes raw material lot. Inventory MUST stay accurate.",
    },
    "ops-performance": {
      label: "OperationsPerformance confirmation",
      from: "Opcenter EX", to: "SAP ERP",
      schema: "OPERATIONS_PERFORMANCE",
      payloadId: "XML-OPPERF-001",
      semantic: "exactly-once",
      sla: "P99 < 30s · finance impact",
      color: T.green,
      desc: "Operation complete. Yield, scrap, labor — every confirm becomes ERP cost data.",
    },
    "equipment": {
      label: "Equipment state change",
      from: "Industrial Edge", to: "Opcenter EX",
      schema: "EQUIPMENT",
      payloadId: "XML-EQUIPMENT-001",
      semantic: "at-least-once",
      sla: "P99 < 5s",
      color: T.orange,
      desc: "Machine state changes (running → fault). MES needs to react.",
    },
  };
  const sc = SCENARIOS[scenario];
  const schema = schemaById(sc.schema);

  // Pipeline stages — each stage is a position on the canvas
  // Stage 1 (Producer) → 8 (Application). Stage 4 has the optional failure gate.
  const STAGES = [
    { id: 1, name: "PRODUCER",       sub: sc.from,                          color: T.cyan,   x: 60,   role: "Emit business event" },
    { id: 2, name: "GATEWAY OUT",    sub: "B2MML serializer · idemp key",   color: T.blue,   x: 230,  role: "Native → canonical XML" },
    { id: 3, name: "XSD VALIDATION", sub: "Schema constraints checked",     color: T.purple, x: 400,  role: "Reject if non-conformant" },
    { id: 4, name: "REGISTRY",       sub: "Version + compatibility",        color: T.amber,  x: 570,  role: "Schema registered? BACKWARD?" },
    { id: 5, name: "TOPIC",          sub: `acks=all · min.insync=2`,         color: sc.color, x: 740,  role: "Persisted & replicated" },
    { id: 6, name: "CONSUMER POLL",  sub: "Receiver pulls batch",           color: T.teal,   x: 910,  role: "Consume from partition" },
    { id: 7, name: "IDEMP CHECK",    sub: "Has this ID been seen?",          color: T.pink,   x: 1080, role: "Dedupe on B2MML <ID>" },
    { id: 8, name: "APPLY",          sub: sc.to,                            color: T.green,  x: 1250, role: "Canonical → native; commit" },
  ];

  const W = 1320, H = 460;
  const Y_MAIN = 220;
  const dur = speed === "slow" ? 11 : speed === "fast" ? 4 : 7;

  // If showFailure, the dot freezes at stage 3 (XSD validation failure)
  // and a red rejection arrow appears.
  const failureStage = 3;

  return (
    <div>
      <div style={{
        padding: "12px 16px", marginBottom: 14,
        background: `${T.green}10`, border: `1px solid ${T.green}30`, borderRadius: 6,
      }}>
        <div style={{ fontSize: 11, color: T.green, fontWeight: 700, letterSpacing: 1, marginBottom: 4 }}>
          🌊 LIVE B2MML PIPELINE · END-TO-END FEDERATION
        </div>
        <div style={{ fontSize: 10, color: T.text2, lineHeight: 1.5 }}>
          Watch a B2MML envelope traverse the federation pipeline: producer → canonical XML → XSD validation →
          schema registry → bus → consumer → idempotency check → applied to target system. This is the runtime
          of every tier-1 cross-level interaction in the architecture.
        </div>
      </div>

      {/* Controls */}
      <div style={{
        display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14,
        padding: "10px 14px", background: T.bg2, borderRadius: 6, border: `1px solid ${T.border}`,
      }}>
        <span style={{ fontSize: 9, color: T.text2, fontFamily: FONTS.mono, letterSpacing: 1 }}>SCENARIO ▸</span>
        {Object.entries(SCENARIOS).map(([k, s]) => (
          <button key={k} onClick={() => setScenario(k)}
            style={chipBtn(scenario === k, s.color)}>
            {s.label}
          </button>
        ))}
        <span style={{ marginLeft: 14, fontSize: 9, color: T.text2, fontFamily: FONTS.mono, letterSpacing: 1 }}>SPEED ▸</span>
        {["slow", "normal", "fast"].map(s => (
          <button key={s} onClick={() => setSpeed(s)}
            style={chipBtn(speed === s, T.cyan)}>{s}</button>
        ))}
        <label style={{
          marginLeft: 14, fontSize: 10, cursor: "pointer",
          color: showFailure ? T.red : T.text2,
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <input type="checkbox" checked={showFailure} onChange={e => setShowFailure(e.target.checked)}/>
          <span>⚠️ Show XSD validation failure (schema drift)</span>
        </label>
      </div>

      {/* Scenario summary card */}
      <div style={{
        background: T.bg2, border: `1px solid ${sc.color}40`,
        borderLeft: `4px solid ${sc.color}`, borderRadius: 6,
        padding: "10px 14px", marginBottom: 14,
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14,
      }}>
        <div>
          <div style={{ fontSize: 8, color: T.text3, letterSpacing: 1, fontFamily: FONTS.mono }}>FLOW</div>
          <div style={{ fontSize: 11, color: T.text, fontWeight: 700 }}>{sc.from} → {sc.to}</div>
        </div>
        <div>
          <div style={{ fontSize: 8, color: T.text3, letterSpacing: 1, fontFamily: FONTS.mono }}>SCHEMA</div>
          <div style={{ fontSize: 11, color: schema?.color, fontWeight: 700 }}>
            {schema?.icon} {schema?.name} <span style={{ fontSize: 9, color: T.text3 }}>({schema?.isaPart})</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 8, color: T.text3, letterSpacing: 1, fontFamily: FONTS.mono }}>SEMANTIC · SLA</div>
          <div style={{ fontSize: 11, color: sc.semantic === "exactly-once" ? T.red : T.amber, fontWeight: 700 }}>
            {sc.semantic} · <span style={{ color: T.green }}>{sc.sla}</span>
          </div>
        </div>
        <div>
          <button onClick={() => { if (setSelectedPayload) setSelectedPayload(sc.payloadId); setView("payloads"); }}
            style={{
              fontSize: 10, padding: "5px 12px", borderRadius: 4,
              background: `${T.amber}15`, color: T.amber,
              border: `1px solid ${T.amber}`, cursor: "pointer",
              fontFamily: FONTS.mono, fontWeight: 700,
            }}>📨 view this XML payload</button>
        </div>
      </div>

      {/* The pipeline canvas */}
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`, borderRadius: 8,
        overflow: "auto", padding: 12, marginBottom: 14,
      }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
          <defs>
            <marker id="lf-arr" viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L10,5 L0,10 Z" fill={sc.color}/>
            </marker>
            <marker id="lf-arr-red" viewBox="0 0 10 10" refX="9" refY="5"
              markerWidth="6" markerHeight="6" orient="auto">
              <path d="M0,0 L10,5 L0,10 Z" fill={T.red}/>
            </marker>
            <filter id="lf-glow">
              <feGaussianBlur stdDeviation="3" result="b"/>
              <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>

          {/* Background lane backdrop */}
          <rect x={20} y={Y_MAIN - 70} width={W - 40} height={140}
            fill={`${sc.color}06`} stroke={`${sc.color}30`} strokeWidth="1" strokeDasharray="3,3" rx={8}/>
          <text x={40} y={Y_MAIN - 50} fontSize="9" fill={sc.color} fontFamily={FONTS.mono} letterSpacing="1" fontWeight="700">
            B2MML PIPELINE · {sc.label.toUpperCase()}
          </text>

          {/* Connecting path */}
          {(() => {
            const pts = STAGES.map(s => `${s.x} ${Y_MAIN}`);
            const d = `M ${pts.join(" L ")}`;
            return (
              <path id="b2mml-pipeline-path" d={d}
                stroke={sc.color} strokeWidth="3" strokeOpacity="0.5"
                fill="none" markerEnd="url(#lf-arr)"/>
            );
          })()}

          {/* Animated B2MML payload envelope */}
          {!showFailure && (
            <g>
              <rect x={-22} y={-13} width={44} height={26} rx={3}
                fill={schema?.color || sc.color} stroke="#000" strokeWidth="1.5"
                filter="url(#lf-glow)">
                <animateMotion dur={`${dur}s`} repeatCount="indefinite" rotate="0">
                  <mpath href="#b2mml-pipeline-path"/>
                </animateMotion>
              </rect>
              <text x={0} y={4} fontSize="9" textAnchor="middle"
                fill="#000" fontWeight="700" fontFamily={FONTS.mono}>
                <animateMotion dur={`${dur}s`} repeatCount="indefinite" rotate="0">
                  <mpath href="#b2mml-pipeline-path"/>
                </animateMotion>
                XML
              </text>
            </g>
          )}

          {/* In failure mode: envelope freezes at stage 3 + red rejection */}
          {showFailure && (
            <g>
              <rect x={STAGES[failureStage - 1].x - 22} y={Y_MAIN - 13} width={44} height={26} rx={3}
                fill={schema?.color || sc.color} stroke={T.red} strokeWidth="2"
                filter="url(#lf-glow)" opacity={0.85}/>
              <text x={STAGES[failureStage - 1].x} y={Y_MAIN + 4} fontSize="9" textAnchor="middle"
                fill="#000" fontWeight="700" fontFamily={FONTS.mono}>XML</text>
              {/* Big red "REJECTED" indicator */}
              <text x={STAGES[failureStage - 1].x} y={Y_MAIN - 30} fontSize="11"
                textAnchor="middle" fill={T.red} fontWeight="900" fontFamily={FONTS.mono}>
                ✕ REJECTED
              </text>
              {/* Rejection arrow back to producer */}
              <path d={`M ${STAGES[failureStage - 1].x} ${Y_MAIN + 15} Q ${STAGES[failureStage - 1].x} ${Y_MAIN + 70}, ${STAGES[0].x} ${Y_MAIN + 50}`}
                fill="none" stroke={T.red} strokeWidth="2" strokeDasharray="5,3"
                markerEnd="url(#lf-arr-red)"/>
              <text x={(STAGES[0].x + STAGES[failureStage - 1].x) / 2} y={Y_MAIN + 85}
                fontSize="9" fill={T.red} fontWeight="700" textAnchor="middle" fontFamily={FONTS.mono}>
                4xx · Producer must re-version
              </text>
            </g>
          )}

          {/* Stages */}
          {STAGES.map((s, i) => {
            const isFailureStage = showFailure && s.id === failureStage;
            const isActive = !showFailure || s.id <= failureStage;
            const ringColor = isFailureStage ? T.red : s.color;
            return (
              <g key={s.id} opacity={isActive ? 1 : 0.35}>
                <circle cx={s.x} cy={Y_MAIN} r={26}
                  fill={`${ringColor}25`} stroke={ringColor} strokeWidth="2"/>
                <text x={s.x} y={Y_MAIN + 5} fontSize="14"
                  textAnchor="middle" fontFamily={FONTS.header} fontWeight="700" fill={ringColor}>
                  {s.id}
                </text>
                {/* Stage name (above) */}
                <text x={s.x} y={Y_MAIN - 40} fontSize="9"
                  textAnchor="middle" fontWeight="700" fontFamily={FONTS.mono} letterSpacing="0.5"
                  fill={isFailureStage ? T.red : T.text}>
                  {s.name}
                </text>
                {/* Subtitle (below) */}
                <text x={s.x} y={Y_MAIN + 50} fontSize="8.5"
                  textAnchor="middle" fill={T.text2} fontStyle="italic">
                  {s.sub}
                </text>
                {/* Role (very bottom) */}
                <text x={s.x} y={Y_MAIN + 64} fontSize="7.5"
                  textAnchor="middle" fill={T.text3} fontFamily={FONTS.mono}>
                  {s.role}
                </text>
              </g>
            );
          })}

          {/* L4/L3 boundary indicator at stage 5 */}
          <g transform={`translate(${(STAGES[4].x + STAGES[3].x) / 2}, ${Y_MAIN - 100})`}>
            <rect x={-40} y={-12} width={80} height={22} rx={4}
              fill={`${T.amber}20`} stroke={T.amber} strokeWidth="1" strokeDasharray="3,2"/>
            <text x={0} y={3} fontSize="8" textAnchor="middle"
              fill={T.amber} fontWeight="700" fontFamily={FONTS.mono} letterSpacing="1">
              FEDERATION
            </text>
            <text x={0} y={32} fontSize="8" textAnchor="middle" fill={T.amber} fontFamily={FONTS.mono}>
              ↑ tier boundary ↓
            </text>
          </g>

          {/* Footer panel */}
          <g transform={`translate(20, ${Y_MAIN + 130})`}>
            <rect x={0} y={0} width={W - 40} height={70} rx={6}
              fill={T.bg3} stroke={T.border} strokeWidth="1"/>
            <text x={14} y={18} fontSize="9" fill={T.cyan} fontFamily={FONTS.mono} fontWeight="700" letterSpacing="1">
              WHAT YOU JUST SAW
            </text>
            <text x={14} y={36} fontSize="9" fill={T.text}>
              {showFailure
                ? "✕ XSD validation rejected the payload. The schema registry refused a non-conformant message. The producer must re-version, the audit log captures the rejection, and the consumer never sees a malformed envelope."
                : `✓ A ${schema?.name} envelope traversed all 8 stages — canonical XML serialization, conformance validation, version check, durable persistence, idempotent consumption, and finally application.`
              }
            </text>
            <text x={14} y={56} fontSize="9" fill={T.text2} fontStyle="italic">
              {showFailure
                ? "This is the #1 source of B2MML production incidents. Schema registry + BACKWARD compatibility prevents 95% of them."
                : "Every cross-tier interaction in CLM runs this pipeline. Per-tier sovereignty + canonical contracts = sustainable federation."
              }
            </text>
          </g>
        </svg>
      </div>

      {/* Three-column key takeaways */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <div style={{
          background: T.bg2, border: `1px solid ${T.cyan}40`,
          borderTop: `3px solid ${T.cyan}`, borderRadius: 6, padding: "12px 14px",
        }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: T.cyan, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            WHAT FEDERATION GUARANTEES
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 9.5, color: T.text2, lineHeight: 1.6 }}>
            <li>Each tier evolves independently</li>
            <li>Canonical XML survives system replacements</li>
            <li>Schema registry catches drift at producer time</li>
            <li>Idempotency on B2MML <code>&lt;ID&gt;</code> prevents duplicate side-effects</li>
            <li>Audit ledger captures full envelope per stage</li>
          </ul>
        </div>
        <div style={{
          background: T.bg2, border: `1px solid ${T.red}40`,
          borderTop: `3px solid ${T.red}`, borderRadius: 6, padding: "12px 14px",
        }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: T.red, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            WHAT FEDERATION FORBIDS
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 9.5, color: T.text2, lineHeight: 1.6 }}>
            <li>Cross-tier database joins</li>
            <li>Shared keys without explicit translation</li>
            <li>Sync API calls across tier boundaries</li>
            <li>Custom XML extensions outside <code>*Property</code></li>
            <li>Disabling XSD validation in "emergency"</li>
          </ul>
        </div>
        <div style={{
          background: T.bg2, border: `1px solid ${T.gold}40`,
          borderTop: `3px solid ${T.gold}`, borderRadius: 6, padding: "12px 14px",
        }}>
          <div style={{ fontFamily: FONTS.mono, fontSize: 9, color: T.gold, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
            STAKEHOLDER QUESTIONS
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 9.5, color: T.text2, lineHeight: 1.6 }}>
            <li>Can we replace SAP without breaking MES? — <strong style={{ color: T.green }}>yes</strong>, federation contract preserves canonical envelope</li>
            <li>Can we audit a transaction? — <strong style={{ color: T.green }}>yes</strong>, B2MML <code>&lt;ID&gt;</code> traces all 8 stages</li>
            <li>What if XSD changes? — <strong style={{ color: T.green }}>BACKWARD</strong> compatibility mode default; consumers tolerate added optional elements</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
